import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import type { RoutePoint } from './mapTypes';

interface MapRoutePreviewProps {
  routePoints: RoutePoint[];
  cellPx: number;
  visible: boolean;
  totalDistance: number;
  /**
   * Adım modunda (step) animasyonu kapatıp tüm noktaları anında göstermek için false geç.
   * Default true (handleRoutePreview davranışı korunur).
   */
  animate?: boolean;
}

const ANIM_INTERVAL = 60;
const COLOR_OUTBOUND = '#1976d2'; // mavi — gidiş
const COLOR_RETURN   = '#e65100'; // turuncu — dönüş

const MapRoutePreview: React.FC<MapRoutePreviewProps> = ({
  routePoints, cellPx, visible, totalDistance, animate = true,
}) => {
  const [animIndex, setAnimIndex] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevPointsRef = useRef<RoutePoint[]>([]);

  const startAnimation = (fromIndex = 0) => {
    setAnimIndex(fromIndex);
    if (timerRef.current) clearInterval(timerRef.current);
    let idx = fromIndex;
    timerRef.current = setInterval(() => {
      idx++;
      if (idx >= routePoints.length) {
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = null;
        return;
      }
      setAnimIndex(idx);
    }, ANIM_INTERVAL);
  };

  useEffect(() => {
    if (!visible || routePoints.length === 0) {
      prevPointsRef.current = routePoints;
      return;
    }

    if (!animate) {
      // Step modu: tüm noktaları anında göster, animasyon yok.
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      setAnimIndex(routePoints.length - 1);
      prevPointsRef.current = routePoints;
      return;
    }

    // Önceki rota yeni rotanın bir prefix'i mi? (uzatma)
    const prev = prevPointsRef.current;
    const isExtension =
      prev.length > 0 &&
      prev.length < routePoints.length &&
      prev.every((p, i) => {
        const c = routePoints[i];
        return c && c.gridX === p.gridX && c.gridY === p.gridY;
      });

    if (isExtension) {
      // Önceki son noktadan devam et — baştan sarmasın.
      startAnimation(prev.length - 1);
    } else {
      startAnimation(0);
    }

    prevPointsRef.current = routePoints;
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, routePoints, animate]);

  if (!visible || routePoints.length === 0) return null;

  const visiblePoints = routePoints.slice(0, animIndex + 1);
  const stopCount = routePoints.filter(p => !!p.label).length;
  const isFinished = animIndex >= routePoints.length - 1;

  return (
    <>
      {/* Route lines — colored by segment */}
      <svg style={{
        position: 'absolute', left: 0, top: 0,
        width: '100%', height: '100%',
        pointerEvents: 'none', zIndex: 35,
        overflow: 'visible',
      }}>
        <defs>
          {/* Arrowhead for outbound */}
          <marker id="arrow-out" markerWidth="6" markerHeight="6"
            refX="5" refY="3" orient="auto">
            <path d="M0,0 L0,6 L6,3 z" fill={COLOR_OUTBOUND} opacity={0.7} />
          </marker>
          {/* Arrowhead for return */}
          <marker id="arrow-ret" markerWidth="6" markerHeight="6"
            refX="5" refY="3" orient="auto">
            <path d="M0,0 L0,6 L6,3 z" fill={COLOR_RETURN} opacity={0.7} />
          </marker>
        </defs>

        {visiblePoints.length > 1 && visiblePoints.map((pt, i) => {
          if (i === 0) return null;
          const prev = visiblePoints[i - 1];
          const isReturn = pt.segment === 'return' || prev.segment === 'return';
          const isConnector = pt.routeStyle === 'connector' || prev.routeStyle === 'connector';
          const color = isReturn ? COLOR_RETURN : COLOR_OUTBOUND;
          const markerId = isReturn ? 'arrow-ret' : 'arrow-out';
          return (
            <line key={`rl-${i}`}
              x1={(prev.gridX + 0.5) * cellPx}
              y1={(prev.gridY + 0.5) * cellPx}
              x2={(pt.gridX + 0.5) * cellPx}
              y2={(pt.gridY + 0.5) * cellPx}
              stroke={color}
              strokeWidth={isConnector ? 1.5 : 2.5}
              strokeLinecap="round"
              opacity={isConnector ? 0.65 : 0.85}
              strokeDasharray={isConnector ? '4 4' : undefined}
              markerEnd={`url(#${markerId})`}
            />
          );
        })}
      </svg>

      {/* Stop pins — only labeled stops */}
      {visiblePoints.map((pt, i) => {
        if (!pt.label) return null;
        const isEntry = pt.label === 'Giriş';
        const isExit  = pt.label === 'Çıkış';
        const bg   = isEntry ? '#4caf50' : isExit ? COLOR_RETURN : COLOR_OUTBOUND;
        const text = isEntry ? 'G' : isExit ? 'Ç' : pt.label;
        return (
          <Box key={`pin-${i}`} sx={{
            position: 'absolute',
            left: (pt.gridX + 0.5) * cellPx,
            top:  (pt.gridY + 0.5) * cellPx,
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            zIndex: 36,
          }}>
            <Box sx={{
              width:  Math.max(16, cellPx * 0.6),
              height: Math.max(16, cellPx * 0.6),
              borderRadius: '50%',
              bgcolor: bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1.5px solid #fff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
            }}>
              <Typography sx={{
                fontSize: Math.max(6, cellPx * 0.25),
                fontWeight: 800, color: '#fff', lineHeight: 1, userSelect: 'none',
              }}>
                {text}
              </Typography>
            </Box>
          </Box>
        );
      })}

      {/* Legend + distance info */}
      {isFinished && (
        <Box sx={{
          position: 'absolute', right: 8, top: 8,
          pointerEvents: 'auto', zIndex: 40,
          bgcolor: 'rgba(255,255,255,0.94)',
          borderRadius: 1, px: 1.5, py: 0.75,
          boxShadow: 2,
          display: 'flex', flexDirection: 'column', gap: 0.5,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" fontWeight={600}>
              Toplam: ~{totalDistance}m
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {stopCount} durak
            </Typography>
            <Tooltip title="Tekrar Oynat">
              <IconButton size="small" onClick={() => startAnimation(0)}>
                <ReplayIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {/* Legend */}
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 20, height: 3, bgcolor: COLOR_OUTBOUND, borderRadius: 1 }} />
              <Typography variant="caption" color="text.secondary">Gidiş</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 20, height: 3, bgcolor: COLOR_RETURN, borderRadius: 1 }} />
              <Typography variant="caption" color="text.secondary">Dönüş</Typography>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default MapRoutePreview;
